/**
 * Mock API responses for development without backend.
 * Used when REACT_APP_ENV=development (unless REACT_APP_USE_MOCK_API=false).
 */

const delay = (ms = 30) => new Promise((r) => setTimeout(r, ms));

// --- Auth ---
export const mockVerifyToken = async () => {
  await delay();
  return { success: true, user_id: 1, clerk_id: "dev_user" };
};

export const mockSyncUser = async () => {
  await delay();
  return { success: true, user: { id: 1, clerk_id: "dev_user", email: "dev@example.com" } };
};

export const mockLogout = async () => {
  await delay();
  return { success: true };
};

// --- Credits ---
export const mockGetPackages = async () => {
  await delay();
  return {
    success: true,
    packages: [
      { id: 1, name: "Starter", credits: 100, price: 4.99, display_order: 1 },
      { id: 2, name: "Pro", credits: 500, price: 19.99, display_order: 2 },
    ],
    total: 2,
  };
};

export const mockGetBalance = async () => {
  await delay();
  const mockZeroCredits =
    process.env.REACT_APP_ENV === "development" &&
    process.env.REACT_APP_MOCK_ZERO_CREDITS === "true";
  return {
    success: true,
    credits: mockZeroCredits ? 0 : 50,
    clerk_id: "dev_user",
  };
};

export const mockGetCreditSummary = async () => {
  await delay();
  return { success: true, lifetime_credits: 50, monthly_credits: 0 };
};

export const mockGetHistory = async () => {
  await delay();
  const now = Date.now();
  const transactions = [
    {
      transaction_id: 1,
      transaction_type: "deduct",
      credits_allotted: 0,
      credits_consumed: 1,
      credit_balance: 49,
      transaction_source: "job",
      reference_id: "job-101",
      remarks: "Deducted 1 credits for job",
      transaction_time: new Date(now - 1 * 3600000).toISOString(),
    },
    {
      transaction_id: 2,
      transaction_type: "deduct",
      credits_allotted: 0,
      credits_consumed: 3,
      credit_balance: 50,
      transaction_source: "job",
      reference_id: "job-102",
      remarks: "Deducted 3 credits for job",
      transaction_time: new Date(now - 5 * 3600000).toISOString(),
    },
    {
      transaction_id: 3,
      transaction_type: "add",
      credits_allotted: 50,
      credits_consumed: 0,
      credit_balance: 53,
      transaction_source: "package",
      reference_id: "pkg-1",
      remarks: "Added 50 credits via package",
      transaction_time: new Date(now - 48 * 3600000).toISOString(),
    },
  ];
  return { success: true, transactions, total: transactions.length };
};

let mockSubscriptionActive = true;

export const mockPurchasePackage = async () => {
  await delay();
  return { success: true, new_balance: 150, message: "Credits added (mock)" };
};

export const mockCreatePaymentOrder = async () => {
  await delay();
  return {
    success: true,
    lz_payment_id: "mock_lz_payment",
    kind: "payg",
    status: "paid",
    amount: 12750,
    currency: "INR",
    credits_added: 100,
    remaining_credits: 150,
    message: "Credits added (mock)",
  };
};

export const mockCreatePaymentSubscription = async () => {
  await delay();
  mockSubscriptionActive = true;
  return {
    success: true,
    lz_payment_id: "mock_lz_payment_sub",
    lz_subscription_id: "mock_lz_sub",
    kind: "subscription",
    status: "paid",
    amount: 51000,
    currency: "INR",
    credits_added: 50,
    remaining_credits: 100,
    message: "Subscription activated (mock)",
  };
};

export const mockConfirmPayment = async () => {
  await delay();
  return { success: true, status: "paid", remaining_credits: 150 };
};

export const mockGetPayment = async () => {
  await delay();
  return { success: true, status: "paid", remaining_credits: 150 };
};

export const mockAddCredits = async () => {
  await delay();
  return { success: true, new_balance: 100 };
};

// --- User ---
export const mockGetProfile = async () => {
  await delay();
  return {
    email: "dev@example.com",
    first_name: "Dev",
    last_name: "User",
    profile_picture: null,
    total_credits: 50,
    subscription_status: "inactive",
    plan_type: null,
    created_at: new Date().toISOString(),
    last_login: new Date().toISOString(),
  };
};

export const mockUpdateProfile = async () => {
  await delay();
  return { success: true };
};

export const mockGetNotifications = async () => {
  await delay();
  return { success: true, notifications: [], total: 0 };
};

export const mockMarkNotificationRead = async () => {
  await delay();
  return { success: true };
};

export const mockMarkAllNotificationsRead = async () => {
  await delay();
  return { success: true };
};

export const mockCheckFirstTime = async () => {
  await delay();
  return { success: true, is_first_time: false, message: "ok" };
};

export const mockMarkWelcomed = async () => {
  await delay();
  return { success: true };
};

export const mockClaimSignupCredits = async () => {
  await delay();
  return { success: true, newBalance: 53, message: "Signup credits claimed (mock)" };
};

export const mockRequestMoreCredits = async () => {
  await delay();
  return { success: true, message: "Request recorded (mock)", click_count: 1 };
};

// --- Subscriptions ---
export const mockGetPlans = async () => {
  await delay();
  return {
    success: true,
    plans: [
      { id: 1, name: "Monthly", credits_per_month: 500, display_order: 1 },
      { id: 2, name: "Yearly", credits_per_month: 1000, display_order: 2 },
    ],
    total: 2,
  };
};

export const mockGetPlanPackages = async () => {
  await delay();
  return {
    success: true,
    packages: [
      { id: 1, name: "Basic", price: 9.99, credits_allotted: 500 },
      { id: 2, name: "Pro", price: 19.99, credits_allotted: 1000 },
    ],
    subscription_id: 1,
    total: 2,
  };
};

export const mockSubscribe = async () => {
  await delay();
  mockSubscriptionActive = true;
  return { success: true, subscription: { id: 1, status: "active" } };
};

export const mockCancelSubscription = async () => {
  await delay();
  mockSubscriptionActive = false;
  return { success: true, message: "Subscription cancelled (mock)" };
};

export const mockGetStatus = async () => {
  await delay();
  const mockZeroCredits =
    process.env.REACT_APP_ENV === "development" &&
    process.env.REACT_APP_MOCK_ZERO_CREDITS === "true";
  if (!mockSubscriptionActive) {
    return {
      success: true,
      subscription: null,
      credits: mockZeroCredits ? 0 : 50,
      lifetime_credits: 50,
      monthly_credits: 0,
      benefits: {
        has_subscription: false,
        plan_name: "Pay as you go",
        monthly_credits: 0,
        remaining_credits: mockZeroCredits ? 0 : 50,
        available_credits: mockZeroCredits ? 0 : 50,
        allocated_storage_bytes: 50 * 1073741824,
        remaining_storage_bytes: 50 * 1073741824,
        allocated_exports: 500,
        remaining_exports: 500,
        benefits: [
          "Pay per use",
          "No commitment",
          "50 GB storage",
          "Credits never expire",
          "Commercial license",
        ],
      },
    };
  }

  return {
    success: true,
    subscription: {
      id: 1,
      package_id: 2,
      status: "active",
      auto_renewal: true,
      start_at: new Date(Date.now() - 5 * 86400000).toISOString(),
      end_at: new Date(Date.now() + 25 * 86400000).toISOString(),
    },
    credits: mockZeroCredits ? 0 : 500,
    lifetime_credits: 500,
    monthly_credits: 500,
    benefits: {
      has_subscription: true,
      plan_name: "Pro Plan",
      plan_family: "pro",
      monthly_credits: 500,
      remaining_credits: mockZeroCredits ? 0 : 500,
      available_credits: mockZeroCredits ? 0 : 500,
      price: 45.48,
      currency: "USD",
      package_id: 2,
      subscription_id: 1,
      start_date: new Date(Date.now() - 5 * 86400000).toISOString(),
      end_date: new Date(Date.now() + 25 * 86400000).toISOString(),
      status: "active",
      auto_renewal: true,
      allocated_storage_bytes: 5 * 1073741824,
      remaining_storage_bytes: 5 * 1073741824,
      allocated_exports: 2000,
      remaining_exports: 2000,
      benefits: [
        "500 Credits per month",
        "5 GB Storage",
        "Max 10 MB file size",
        "Full HD",
      ],
    },
  };
};

export const mockGetSubscriptionHistory = async () => {
  await delay();
  return { success: true, history: [], total: 0 };
};

export const mockGetBillingHistory = async () => {
  await delay();
  const history = [
    {
      date: "Oct 16, 2025 16:36:05",
      amount: "$45.00",
      plan: "Pro",
      credits: 500,
      type: "subscription",
      status: "Paid",
    },
    {
      date: "Oct 15, 2025 13:48:25",
      amount: "$8.99",
      plan: "Pay as you go",
      credits: 10,
      type: "credit",
      status: "Paid",
    },
    {
      date: "Oct 12, 2025 11:35:45",
      amount: "$10.00",
      plan: "Lite",
      credits: 100,
      type: "subscription",
      status: "Paid",
    },
    {
      date: "Oct 08, 2025 09:12:01",
      amount: "$650.00",
      plan: "Max",
      credits: 10000,
      type: "subscription",
      status: "Paid",
    },
  ];
  return { success: true, history, total: history.length };
};

export const mockGetBenefits = async () => {
  await delay();
  return { success: true, benefits: { monthly_credits: 0, lifetime_credits: 50 } };
};

// --- Background removal ---
/** In-memory creations for mock upload → editor testing (no backend / no AI). */
const mockCreations = [];

const trimMockCreations = () => {
  while (mockCreations.length > 20) {
    const removed = mockCreations.pop();
    if (removed?.output_url?.startsWith("blob:")) {
      URL.revokeObjectURL(removed.output_url);
    }
  }
};

/**
 * Passthrough single BG remove: returns the uploaded file as output (no AI).
 * Upload a PNG with transparency to test canvas object/background picking locally.
 */
export const mockRemoveBackgroundSingle = async (imageFile) => {
  await delay(400);
  const id = mockCreations.length > 0 ? Math.max(...mockCreations.map((c) => c.id)) + 1 : 1;
  const outputUrl = URL.createObjectURL(imageFile);
  mockCreations.unshift({
    id,
    file: imageFile,
    output_url: outputUrl,
    created_at: new Date().toISOString(),
  });
  trimMockCreations();
  return { success: true, output_url: outputUrl, creation_id: id };
};

export const mockFetchCreationImageBlob = async (_token, creationId) => {
  await delay();
  const creation = mockCreations.find((c) => c.id === Number(creationId));
  if (!creation?.file) {
    throw new Error(`Mock creation ${creationId} not found`);
  }
  return creation.file;
};

export const mockTestAuth = async () => {
  await delay();
  return { success: true, message: "Auth OK (mock)" };
};

export const mockGetJobStatus = async () => {
  await delay();
  return { status: "completed", result: { output_url: "" } };
};

export const mockGetBatchStatus = async () => {
  await delay();
  return { status: "completed", results: [] };
};

export const mockGetCreations = async () => {
  await delay();
  return {
    success: true,
    creations: mockCreations.map((c) => ({
      id: c.id,
      kind: "creation",
      creation_id: c.id,
      file_type: "image",
      output_url: c.output_url,
      output_format: "png",
      created_at: c.created_at,
      file_size_mb: Number((c.file.size / (1024 * 1024)).toFixed(2)),
    })),
    exports: [],
    total: mockCreations.length,
  };
};

export const mockGetCreationDownloadUrl = async () => {
  await delay();
  return { success: true, download_url: "#", expires_in: 3600 };
};

export const mockDeleteCreation = async () => {
  await delay();
  return { success: true };
};
